"use client";

import React, { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import imageCompression from "browser-image-compression";

const fileTypes = ["image/png", "image/jpg", "image/jpeg"];
// Allow larger originals (up to 10MB) and rely on compression to reduce size
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface ListingProps {
  onChange: (value: string) => void;
  value: string;
  setIsUploading: (isUploading: boolean) => void;
  listingId?: string;
}

const ListingPhotoUpload: React.FC<ListingProps> = ({
  onChange,
  value,
  setIsUploading,
  listingId,
}) => {
  const [previewURL, setPreviewURL] = useState<string | null>(value || null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (value && value !== previewURL) {
      setPreviewURL(value);
    }

    return () => {
      if (previewURL && previewURL.startsWith('blob:')) {
        URL.revokeObjectURL(previewURL);
      }
    };
  }, [value, previewURL]);

  const validateAndUploadFile = useCallback(
    async (file: File) => {
      setUploadStatus(null);
      setUploadProgress(0);

      const originalImageURL = value;

      if (file.size > MAX_FILE_SIZE) {
        setUploadStatus(`File is too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`);
        return;
      }

      if (!fileTypes.includes(file.type)) {
        setUploadStatus("Invalid file type. Only PNG, JPG, and JPEG are allowed.");
        return;
      }

      const options = {
        // Target around 400KB; this is a good balance between quality and weight
        maxSizeMB: 0.4,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
      };

      try {
        const compressedFile = await imageCompression(file, options);

        const localPreviewURL = URL.createObjectURL(compressedFile);
        setPreviewURL(localPreviewURL);

        const cleanFileName = file.name.replace(/[^\w.-]/gi, "").replace(/\s+/g, "_");
        const objectName = `images/${uuidv4()}_${cleanFileName}`;

        setUploadStatus("Uploading... Please wait until it's uploaded");
        setIsUploading(true);
        setUploadProgress(10);

        const formData = new FormData();
        formData.append("file", compressedFile);
        formData.append("objectName", objectName);

        if (originalImageURL) {
          formData.append("oldImageURL", originalImageURL);
        }

        if (listingId) {
          formData.append("listingId", listingId);
        }

        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            const newProgress = prev + 10;
            return newProgress >= 90 ? 90 : newProgress;
          });
        }, 500);

        const response = await axios.post("/api/minio/uploadlistingphoto", formData);

        clearInterval(progressInterval);
        setIsUploading(false);

        if (response.data.success) {
          setUploadProgress(100);
          setUploadStatus("Uploaded successfully");

          URL.revokeObjectURL(localPreviewURL);

          const newImageURL = response.data.imageURL;
          setPreviewURL(newImageURL);
          onChange(newImageURL);
        } else {
          setUploadStatus("Error uploading");
          setUploadProgress(0);

          if (originalImageURL) {
            setPreviewURL(originalImageURL);
          } else {
            setPreviewURL(null);
          }
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        setUploadStatus("Error uploading");
        setUploadProgress(0);
        setIsUploading(false);

        if (originalImageURL) {
          setPreviewURL(originalImageURL);
        } else {
          setPreviewURL(null);
        }
      }
    },
    [onChange, setIsUploading, listingId, value]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      validateAndUploadFile(files[0]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageError = () => {
    setUploadStatus("Error displaying image preview");
  };

  const onDropZoneDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes("Files")) {
      setIsDragging(true);
    }
  };

  const onDropZoneDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget as Node | null;
    if (!next || !e.currentTarget.contains(next)) {
      setIsDragging(false);
    }
  };

  const onDropZoneDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };

  const onDropZoneDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) validateAndUploadFile(f);
  };

  return (
    <div className="w-full flex flex-col items-center space-y-4">
      {/* Custom file uploader UI */}
      <div
        onDragEnter={onDropZoneDragEnter}
        onDragLeave={onDropZoneDragLeave}
        onDragOver={onDropZoneDragOver}
        onDrop={onDropZoneDrop}
        className={`w-full border-2 border-dashed rounded-lg p-6 text-center transition-colors ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400"
          }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".png,.jpg,.jpeg"
          className="hidden"
        />
        <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
          <svg
            className="w-10 h-10 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-sm text-gray-600">Drag & drop your image here</p>
          <p className="text-xs text-gray-500">PNG, JPG, JPEG (max 10MB before compression)</p>
        </div>
        <button
          type="button"
          onClick={handleBrowseClick}
          className="mt-4 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Choose file
        </button>
      </div>

      {/* Image preview with error handling */}
      {previewURL && !uploadStatus?.includes("too large") && !uploadStatus?.includes("Invalid file type") && (
        <div className="w-full max-w-md h-64 overflow-hidden">
          <img
            src={previewURL}
            alt="Listing preview"
            className="w-full h-auto rounded-md shadow-md object-contain"
            onError={handleImageError}
          />
        </div>
      )}

      {/* Status message */}
      {uploadStatus && (
        <div
          className={`mt-2 text-sm font-medium ${uploadStatus.includes("Error") ||
              uploadStatus.includes("too large") ||
              uploadStatus.includes("Invalid file type")
              ? "text-red-500"
              : uploadStatus.includes("Uploading")
                ? "text-blue-500"
                : "text-green-500"
            }`}
        >
          {uploadStatus}
        </div>
      )}

      {/* Progress bar */}
      {uploadProgress > 0 && !uploadStatus?.includes("too large") && !uploadStatus?.includes("Invalid file type") && (
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-cyan-600 h-2.5 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default ListingPhotoUpload;